import React, { useEffect } from 'react';
import { SegmentService } from 'analytics';
import { SliceContext } from 'components/Slices/types';
import { usePlayer } from '@landr/react-player';
import { prismicText } from 'helpers';
import { Versions } from '../types';
import { AudioPlayersProgress } from './AudioPlayersProgress';

export type AudioPlayersProps = {
    playerNumber: number;
    audioStyles: any;
    activeVersion: Versions;
    activeStyle: number;
    isPlaying: boolean;
    sliceContext: SliceContext;
    isSinglePlayer: boolean | undefined;
    showSpinner: () => void;
    hideSpinner: () => void;
    circleProgress: AudioPlayersProgress | undefined;
};

export const AudioPlayers: React.FC<AudioPlayersProps> = ({
    playerNumber,
    audioStyles,
    activeVersion,
    activeStyle,
    isPlaying,
    sliceContext,
    isSinglePlayer,
    showSpinner,
    hideSpinner,
    circleProgress,
}) => {
    const player = usePlayer(`player_${playerNumber}`);

    useEffect(
        function setSources() {
            const playerSources: any = [];
            if (isSinglePlayer !== undefined) {
                playerSources.push({
                    id: `v-original`,
                    url: audioStyles.mastering_demos_original.url,
                    preload: true,
                });
                if (!isSinglePlayer) {
                    playerSources.push({
                        id: `v-mastered`,
                        url: audioStyles.mastering_demos_mastered.url,
                        preload: true,
                    });
                }
                player.setSources(playerSources);
                player.setActiveSourceId('v-original');
                circleProgress && circleProgress.updateSource(player);
            }
        },
        [audioStyles, isSinglePlayer],
    );

    useEffect(
        function setLoading() {
            player.loading ? showSpinner() : hideSpinner();
        },
        [player.loading],
    );

    useEffect(
        function setPlaying() {
            if (activeStyle === playerNumber) {
                isPlaying ? player.setPlaying(true) : player.setPlaying(false);
                isPlaying && circleProgress && circleProgress.startProgress();
                const activeStyleName: string = audioStyles.mastering_demos_style.name;
                const activeAudioLink: string =
                    activeVersion === 'original'
                        ? audioStyles.mastering_demos_original.url
                        : audioStyles.mastering_demos_mastered.url;
                SegmentService.trackAudioPlayerLaunched(
                    {
                        'L - Destination Link': prismicText(activeAudioLink),
                        'L - Asset Genre': prismicText(activeStyleName),
                    },
                    sliceContext,
                );
            }
        },
        [isPlaying],
    );

    useEffect(
        function setProgress() {
            circleProgress && circleProgress.update(player.currentTime, player.totalTime);
        },
        [player.currentTime, player.totalTime],
    );

    useEffect(
        function setVersion() {
            player.setActiveSourceId(`v-${activeVersion}`);
        },
        [activeVersion],
    );

    useEffect(
        function destroyProgress() {
            circleProgress && circleProgress.destroy();
        },
        [activeStyle],
    );

    return <></>;
};
